import {
  datamuseQuery,
  type PartOfSpeech,
  splitEcho,
  type WordResult,
} from "@/lib/tool-kit/vocabulary/datamuse";

/**
 * The Collocation Finder: which words keep company with a word, one position to the
 * left or right of it.
 *
 * Datamuse answers "which words appear right before / right after this one?" from word
 * pairs counted in Google Books Ngrams. Its `lc` ("left context") and `rc` ("right
 * context") hints rank candidates by how often they sit beside the given word; a bare
 * `sp=*` ("any word") makes them the whole question:
 *
 * - `lc=conduct` — words that follow *conduct*: research, experiments, interviews;
 * - `rc=attention` — words that come right before *attention*: pay, draw, call, focus.
 *
 * The answer is raw pair counts, so it is full of "the", "of" and "is". The finder
 * drops function words by default, then keeps only the part of speech the chosen
 * pattern asks for (verbs before a noun, nouns after a verb, adverbs before an
 * adjective). Datamuse tags each word with its likely parts of speech, most likely
 * first, from the same books. A word counts as the wanted part of speech when that is its
 * first tag, or when it is a noun/verb pair (research, focus) with the wanted one second.
 * The tags are loose, so a few results are the wrong kind and a few words (an irregular
 * past form like "drew") are missing, which the page says plainly. Only adjacent words are counted: "make a decision" has an article
 * between its verb and noun, so it is found from "make" (decisions) but not from
 * "decision".
 */

export type CollocationMode =
  | "nounsAfterVerb"
  | "verbsBeforeNoun"
  | "verbsAfterNoun"
  | "adverbsBeforeAdjective"
  | "anyAfter"
  | "anyBefore";

export type Direction = "after" | "before";

export interface ModeDefinition {
  direction: Direction;
  /** The part of speech to keep; null keeps every content word. */
  pos: PartOfSpeech | null;
  /** The mode a clicked result is explored in, from the result's own side. */
  next: CollocationMode;
}

export const COLLOCATION_MODES: Readonly<
  Record<CollocationMode, ModeDefinition>
> = {
  nounsAfterVerb: { direction: "after", pos: "n", next: "verbsBeforeNoun" },
  verbsBeforeNoun: { direction: "before", pos: "v", next: "nounsAfterVerb" },
  verbsAfterNoun: { direction: "after", pos: "v", next: "nounsAfterVerb" },
  adverbsBeforeAdjective: {
    direction: "before",
    pos: "adv",
    next: "anyAfter",
  },
  anyAfter: { direction: "after", pos: null, next: "anyBefore" },
  anyBefore: { direction: "before", pos: null, next: "anyAfter" },
};

export const MODE_ORDER: readonly CollocationMode[] = [
  "nounsAfterVerb",
  "verbsBeforeNoun",
  "verbsAfterNoun",
  "adverbsBeforeAdjective",
  "anyAfter",
  "anyBefore",
];

const MAX_RESULTS = 300;
const MAX_SHOWN = 60;

/**
 * Words that are grammar rather than vocabulary: determiners, prepositions, pronouns,
 * conjunctions, auxiliaries and modals, plus negation. They top every pair count and say
 * nothing about a word's habits, so they are hidden unless asked for.
 */
const FUNCTION_WORDS: ReadonlySet<string> = new Set(
  `a an the this that these those my your his her its our their some any no every each all both either neither such another
of in on at by for with from to into onto upon over under between among through during before after about against as than without within across toward towards per via like
and or but nor so yet if because while although though whether when where which who whom whose what how why then there here
i me you he him she it we us they them himself herself itself themselves ourselves myself yourself one
be am is are was were been being have has had having do does did done will would shall should can could may might must ought
not n't up out off just also than`.split(/\s+/)
);

const RE_WORDLIKE = /^[a-z][a-z'-]*$/i;

/** Does Datamuse's tag list (likeliest first) make a word the wanted part of speech? */
export function hasPos(tags: readonly PartOfSpeech[], wanted: PartOfSpeech) {
  if (tags[0] === wanted) {
    return true;
  }
  const pair = wanted === "v" ? "n" : "v";
  return (
    (wanted === "v" || wanted === "n") &&
    tags[0] === pair &&
    tags.includes(wanted)
  );
}

export function isFunctionWord(word: string): boolean {
  return FUNCTION_WORDS.has(word.toLowerCase());
}

/** The request: every word that sits beside `word` on the chosen side. */
export function collocationQuery(word: string, mode: CollocationMode): string {
  const side = COLLOCATION_MODES[mode].direction === "after" ? "lc" : "rc";
  return datamuseQuery({ sp: "*", [side]: word, md: "pf", max: MAX_RESULTS });
}

/** The searched word, from an answered query string. */
export function wordOfCollocationQuery(query: string): string {
  const params = new URLSearchParams(query);
  return params.get("lc") ?? params.get("rc") ?? "";
}

/** Which side an answered query asked about, or null. */
export function directionOfQuery(query: string): Direction | null {
  const params = new URLSearchParams(query);
  if (params.has("lc")) {
    return "after";
  }
  return params.has("rc") ? "before" : null;
}

export interface Collocate {
  word: string;
  /** The word as it reads in a pair: `conduct research`, `statistically significant`. */
  phrase: string;
  /** Occurrences per million words of English, when Datamuse knows it. */
  frequency: number | null;
}

/** The two words in reading order. */
export function phraseOf(
  word: string,
  collocate: string,
  direction: Direction
): string {
  return direction === "after"
    ? `${word} ${collocate}`
    : `${collocate} ${word}`;
}

export interface CollocationOptions {
  /** Keep determiners, prepositions, auxiliaries and the like. */
  includeFunctionWords: boolean;
}

/**
 * Datamuse's answer → the collocates a pattern asks for: function words dropped (unless
 * asked for), only the wanted part of speech kept, the searched word and punctuation
 * removed, repeats merged, and the strongest pairs first, as Datamuse ranks them.
 */
export function collocatesOf(
  word: string,
  mode: CollocationMode,
  results: readonly WordResult[],
  options: CollocationOptions
): Collocate[] {
  const { direction, pos } = COLLOCATION_MODES[mode];
  const searched = word.toLowerCase();
  const seen = new Set<string>([searched]);
  const out: Collocate[] = [];
  for (const result of splitEcho(results).results) {
    const key = result.word.toLowerCase();
    if (
      seen.has(key) ||
      !RE_WORDLIKE.test(result.word) ||
      (!options.includeFunctionWords && isFunctionWord(key)) ||
      (pos !== null && !hasPos(result.pos, pos))
    ) {
      continue;
    }
    seen.add(key);
    out.push({
      word: result.word,
      phrase: phraseOf(word, result.word, direction),
      frequency: result.frequency,
    });
    if (out.length >= MAX_SHOWN) {
      break;
    }
  }
  return out;
}
